import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { CachedMicrolink } from "components/common/CachedMicrolink";
import debounce from "lodash.debounce";

import { SkedIcon } from "ui";
import { Link } from "ui/Link";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "components/ui/dialog";
import { Input } from "components/ui/input";
import { Button } from "components/ui/button";
import { hideModal } from "actions/modal";
import { editPostUrl, reloadPosts } from "actions/postsView";
import { isUrl } from "utils/strings";
import { Post as PostType } from "features/post/types";

interface SkedUrlModalProps {
  post: PostType;
  isSkedLinkActive: boolean;
}

const SkedUrlModal = ({
  post,
  isSkedLinkActive,
}: SkedUrlModalProps): JSX.Element => {
  const dispatch = useDispatch();

  const [urlValue, setUrlValue] = useState(post.redirectUrl || "");
  const [isValidUrl, setIsValidUrl] = useState(() => isUrl(urlValue));

  const handleChangeDebounced = debounce((url) => {
    const isUrlValid = isUrl(url);
    setIsValidUrl(isUrlValid);
  }, 1000);

  const handleSwitchClick = () => {
    dispatch(editPostUrl({ ...post, redirectUrl: urlValue }));
    dispatch(hideModal());
    dispatch(reloadPosts());
  };

  const handleClose = () => {
    dispatch(hideModal());
  };

  const handleTextInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const url = e.target.value?.trim();
    setUrlValue(url);
    handleChangeDebounced(url);
  };

  return (
    <Dialog open={true} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-2xl border-neutral-200">
        <DialogHeader>
          <DialogTitle className="font-gotham text-xl font-semibold text-neutral-900">
            {post.redirectUrl ? "Update URL" : "Add a URL"} to this post in
            SkedLink
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 font-inter text-sm text-neutral-700">
          <p>
            {`Outdated product or website link in your SkedLink ${
              !isSkedLinkActive ? "(link in bio tool)" : ""
            }? Update the URL that this post directs to.`}
          </p>

          {!isSkedLinkActive && (
            <div className="flex gap-2 rounded-md bg-blue-50 p-3">
              <SkedIcon
                icon="info"
                size="18"
                className="shrink-0 text-blue-500"
              />
              <p className="text-sm text-blue-600 leading-relaxed">
                This feature is for SkedLink - Sked's link in bio tool. It
                appears you haven't set it up yet (or maybe you're using a link
                shortener). You'll still be able to save a URL, it just won't be
                active until your SkedLink is active.{" "}
                <Link
                  href="https://help.skedsocial.com/en/articles/3811771-getting-started-with-sked-link"
                  target="_blank"
                  underline="always"
                  className="text-blue-600 underline hover:text-blue-700"
                >
                  Read how to set up SkedLink here
                </Link>
                .
              </p>
            </div>
          )}

          <p>
            {post.redirectUrl ? "Update URL" : "Add a URL"} here (website or
            product URL for this post{" "}
            <Link
              href="https://help.skedsocial.com/en/articles/4129487-how-links-work-in-sked"
              target="_blank"
              className="text-blue-500 underline hover:text-blue-600"
            >
              read about how links work
            </Link>
            ):
          </p>

          <Input
            onChange={handleTextInputChange}
            placeholder="URL for this post (include http:// or https://)"
            value={urlValue}
            className="font-inter text-sm border-neutral-200"
          />

          {isValidUrl && (
            <div className="my-4">
              <CachedMicrolink url={urlValue} className="[&_a]:max-w-none" />
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={handleClose}
            className="border-neutral-200 hover:border-neutral-300"
          >
            Cancel
          </Button>
          <Button variant="blue-primary" onClick={handleSwitchClick}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SkedUrlModal;
